// TrueType / OpenType font programs: glyph outlines (glyf), widths, the cmap and post tables.

import { MAC_GLYPH_NAMES } from "./glyphlist";

/** A glyph outline: a flat command list — 0 x y (move), 1 x y (line), 2 cx cy x y (quad), 3 c1x c1y c2x c2y x y (cubic), 4 (close) — in font units. */
export type GlyphPath = number[];

export interface FontProgram {
    unitsPerEm: number;
    numGlyphs: number;
    /** The outline of a glyph, or null when it has none. */
    glyph(gid: number): GlyphPath | null;
    /** The advance in font units, or -1 when unknown. */
    advance(gid: number): number;
    /** The glyph id for a glyph name, or -1. */
    gidByName(name: string): number;
    /** The glyph id of a Unicode value through the cmap, or 0. */
    gidByUnicode(u: number): number;
}

interface CmapSub { platform: number; encoding: number; off: number; format: number }

export class TrueTypeFont implements FontProgram {
    private dv: DataView;
    private tables = new Map<string, { off: number; len: number }>();
    unitsPerEm = 1000;
    numGlyphs = 0;
    private locaLong = false;
    private numHMetrics = 0;
    private cmaps: CmapSub[] = [];
    private names: string[] | null = null;
    private nameIndex: Map<string, number> | null = null;
    private cache = new Map<number, GlyphPath | null>();
    /** The OpenType CFF table, when this is an OTTO font. */
    cffTable: Uint8Array | null = null;

    constructor(public readonly data: Uint8Array) {
        this.dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
        let base = 0;
        const tag = this.tag(0);
        if (tag === "ttcf") base = this.dv.getUint32(12);
        const n = this.dv.getUint16(base + 4);
        for (let i = 0; i < n; i++) {
            const e = base + 12 + i * 16;
            if (e + 16 > data.length) break;
            this.tables.set(this.tag(e), { off: this.dv.getUint32(e + 8), len: this.dv.getUint32(e + 12) });
        }
        const head = this.tables.get("head");
        if (head && head.off + 54 <= data.length) { this.unitsPerEm = this.dv.getUint16(head.off + 18) || 1000; this.locaLong = this.dv.getInt16(head.off + 50) !== 0; }
        const maxp = this.tables.get("maxp");
        if (maxp) this.numGlyphs = this.dv.getUint16(maxp.off + 4);
        const hhea = this.tables.get("hhea");
        if (hhea) this.numHMetrics = this.dv.getUint16(hhea.off + 34);
        const cff = this.tables.get("CFF ");
        if (cff) this.cffTable = data.subarray(cff.off, cff.off + cff.len);
        this.readCmap();
        if (!this.numGlyphs) { const loca = this.tables.get("loca"); if (loca) this.numGlyphs = (loca.len >> (this.locaLong ? 2 : 1)) - 1; }
    }

    /** The raw bytes of a table, or null when the font has none. */
    tableData(tag: string): Uint8Array | null {
        const t = this.tables.get(tag);
        return t && t.off + t.len <= this.data.length ? this.data.subarray(t.off, t.off + t.len) : null;
    }

    /** The font's bounding box (xMin, yMin, xMax, yMax) in font units. */
    get headBox(): [number, number, number, number] {
        const h = this.tables.get("head");
        return h && h.off + 44 <= this.data.length ? [this.dv.getInt16(h.off + 36), this.dv.getInt16(h.off + 38), this.dv.getInt16(h.off + 40), this.dv.getInt16(h.off + 42)] : [0, -200, 1000, 800];
    }

    /** Ascent and descent from the horizontal header, in font units. */
    get lineMetrics(): { ascent: number; descent: number } {
        const h = this.tables.get("hhea");
        return h && h.off + 8 <= this.data.length ? { ascent: this.dv.getInt16(h.off + 4), descent: this.dv.getInt16(h.off + 6) } : { ascent: 800, descent: -200 };
    }

    get hasOutlines(): boolean { return this.tables.has("glyf") && this.tables.has("loca"); }

    private tag(o: number): string { return String.fromCharCode(this.data[o]!, this.data[o + 1]!, this.data[o + 2]!, this.data[o + 3]!); }

    private readCmap() {
        const t = this.tables.get("cmap");
        if (!t) return;
        const n = this.dv.getUint16(t.off + 2);
        for (let i = 0; i < n; i++) {
            const e = t.off + 4 + i * 8;
            if (e + 8 > this.data.length) break;
            const off = t.off + this.dv.getUint32(e + 4);
            if (off + 2 > this.data.length) continue;
            this.cmaps.push({ platform: this.dv.getUint16(e), encoding: this.dv.getUint16(e + 2), off, format: this.dv.getUint16(off) });
        }
    }

    hasCmap(platform: number, encoding: number): boolean { return this.cmaps.some((c) => c.platform === platform && c.encoding === encoding); }
    get cmapCount(): number { return this.cmaps.length; }

    /** Looks a code up in one cmap subtable (0 when absent). */
    lookup(platform: number, encoding: number, code: number): number {
        const c = this.cmaps.find((x) => x.platform === platform && x.encoding === encoding);
        return c ? this.lookupSub(c, code) : 0;
    }

    private lookupSub(c: CmapSub, code: number): number {
        const dv = this.dv, o = c.off;
        try {
            switch (c.format) {
                case 0: return code < 256 ? this.data[o + 6 + code]! : 0;
                case 4: {
                    const segX2 = dv.getUint16(o + 6);
                    const endO = o + 14, startO = endO + segX2 + 2, deltaO = startO + segX2, rangeO = deltaO + segX2;
                    let lo = 0, hi = segX2 / 2 - 1;
                    while (lo <= hi) {
                        const mid = (lo + hi) >> 1;
                        const end = dv.getUint16(endO + mid * 2);
                        if (code > end) lo = mid + 1;
                        else {
                            const start = dv.getUint16(startO + mid * 2);
                            if (code < start) hi = mid - 1;
                            else {
                                const delta = dv.getInt16(deltaO + mid * 2), ro = dv.getUint16(rangeO + mid * 2);
                                if (ro === 0) return (code + delta) & 0xffff;
                                const g = dv.getUint16(rangeO + mid * 2 + ro + (code - start) * 2);
                                return g === 0 ? 0 : (g + delta) & 0xffff;
                            }
                        }
                    }
                    return 0;
                }
                case 6: { const first = dv.getUint16(o + 6), cnt = dv.getUint16(o + 8); return code >= first && code < first + cnt ? dv.getUint16(o + 10 + (code - first) * 2) : 0; }
                case 12: case 13: {
                    const n = dv.getUint32(o + 12);
                    let lo = 0, hi = n - 1;
                    while (lo <= hi) {
                        const mid = (lo + hi) >> 1;
                        const g = o + 16 + mid * 12;
                        const s = dv.getUint32(g), e = dv.getUint32(g + 4);
                        if (code < s) hi = mid - 1; else if (code > e) lo = mid + 1; else return c.format === 12 ? dv.getUint32(g + 8) + (code - s) : dv.getUint32(g + 8);
                    }
                    return 0;
                }
                case 2: {
                    // high-byte mapping for CJK: single-byte codes only handled
                    const hi = (code >> 8) & 255, lo = code & 255;
                    const k = dv.getUint16(o + 6 + hi * 2) / 8;
                    const sh = o + 518 + k * 8;
                    const first = dv.getUint16(sh), cnt = dv.getUint16(sh + 2), delta = dv.getInt16(sh + 4), ro = dv.getUint16(sh + 6);
                    if (lo < first || lo >= first + cnt) return 0;
                    const g = dv.getUint16(sh + 6 + ro + (lo - first) * 2);
                    return g ? (g + delta) & 0xffff : 0;
                }
            }
        } catch { /* out of range */ }
        return 0;
    }

    gidByUnicode(u: number): number {
        for (const [p, e] of [[3, 10], [0, 4], [3, 1], [0, 3], [0, 6], [0, 2], [0, 1], [0, 0]] as const) { const g = this.lookup(p, e, u); if (g) return g; }
        return 0;
    }

    private loadNames() {
        if (this.names) return;
        this.names = [];
        this.nameIndex = new Map();
        const t = this.tables.get("post");
        if (!t || t.len < 32) return;
        const fmt = this.dv.getUint32(t.off);
        if (fmt === 0x00010000) { this.names = MAC_GLYPH_NAMES.slice(0, 258); }
        else if (fmt === 0x00020000) {
            const n = this.dv.getUint16(t.off + 32);
            const idx: number[] = [];
            for (let i = 0; i < n; i++) idx.push(this.dv.getUint16(t.off + 34 + i * 2));
            const strs: string[] = [];
            let p = t.off + 34 + n * 2;
            const end = t.off + t.len;
            while (p < end) { const l = this.data[p++]!; let s = ""; for (let i = 0; i < l && p < end; i++) s += String.fromCharCode(this.data[p++]!); strs.push(s); }
            this.names = idx.map((i) => (i < 258 ? MAC_GLYPH_NAMES[i]! : strs[i - 258] ?? ""));
        }
        this.names.forEach((nm, i) => { if (nm && !this.nameIndex!.has(nm)) this.nameIndex!.set(nm, i); });
    }

    gidByName(name: string): number {
        this.loadNames();
        return this.nameIndex!.get(name) ?? -1;
    }

    advance(gid: number): number {
        const t = this.tables.get("hmtx");
        if (!t || !this.numHMetrics) return -1;
        const i = Math.min(gid, this.numHMetrics - 1);
        const o = t.off + i * 4;
        return o + 2 <= this.data.length ? this.dv.getUint16(o) : -1;
    }

    glyph(gid: number): GlyphPath | null {
        if (this.cache.has(gid)) return this.cache.get(gid)!;
        let p: GlyphPath | null = null;
        try { p = this.load(gid, 0); } catch { p = null; }
        if (this.cache.size > 4000) this.cache.clear();
        this.cache.set(gid, p);
        return p;
    }

    private load(gid: number, depth: number): GlyphPath | null {
        const loca = this.tables.get("loca"), glyf = this.tables.get("glyf");
        if (!loca || !glyf || gid < 0 || gid >= this.numGlyphs || depth > 6) return null;
        const o0 = this.locaLong ? this.dv.getUint32(loca.off + gid * 4) : this.dv.getUint16(loca.off + gid * 2) * 2;
        const o1 = this.locaLong ? this.dv.getUint32(loca.off + gid * 4 + 4) : this.dv.getUint16(loca.off + gid * 2 + 2) * 2;
        if (o1 <= o0) return null;
        const dv = this.dv;
        let p = glyf.off + o0;
        const nc = dv.getInt16(p);
        p += 10;
        if (nc >= 0) {
            const ends: number[] = [];
            for (let i = 0; i < nc; i++) { ends.push(dv.getUint16(p)); p += 2; }
            const np = nc ? ends[nc - 1]! + 1 : 0;
            const il = dv.getUint16(p);
            p += 2 + il;
            const flags: number[] = [];
            while (flags.length < np) {
                const f = this.data[p++]!;
                flags.push(f);
                if (f & 8) { let r = this.data[p++]!; while (r-- > 0 && flags.length < np) flags.push(f); }
            }
            const xs: number[] = [], ys: number[] = [];
            let v = 0;
            for (let i = 0; i < np; i++) {
                const f = flags[i]!;
                if (f & 2) { const d = this.data[p++]!; v += f & 16 ? d : -d; } else if (!(f & 16)) { v += dv.getInt16(p); p += 2; }
                xs.push(v);
            }
            v = 0;
            for (let i = 0; i < np; i++) {
                const f = flags[i]!;
                if (f & 4) { const d = this.data[p++]!; v += f & 32 ? d : -d; } else if (!(f & 32)) { v += dv.getInt16(p); p += 2; }
                ys.push(v);
            }
            const out: GlyphPath = [];
            let s = 0;
            for (const e of ends) {
                contour(out, xs, ys, flags, s, e);
                s = e + 1;
            }
            return out;
        }
        // composite
        const out: GlyphPath = [];
        for (;;) {
            const fl = dv.getUint16(p), gi = dv.getUint16(p + 2);
            p += 4;
            let dx: number, dy: number;
            if (fl & 1) { dx = dv.getInt16(p); dy = dv.getInt16(p + 2); p += 4; } else { dx = dv.getInt8(p); dy = dv.getInt8(p + 1); p += 2; }
            if (!(fl & 2)) { dx = 0; dy = 0; }
            let a = 1, b = 0, c = 0, d = 1;
            if (fl & 8) { a = d = dv.getInt16(p) / 16384; p += 2; }
            else if (fl & 0x40) { a = dv.getInt16(p) / 16384; d = dv.getInt16(p + 2) / 16384; p += 4; }
            else if (fl & 0x80) { a = dv.getInt16(p) / 16384; b = dv.getInt16(p + 2) / 16384; c = dv.getInt16(p + 4) / 16384; d = dv.getInt16(p + 6) / 16384; p += 8; }
            const sub = this.load(gi, depth + 1);
            if (sub) {
                let i = 0;
                while (i < sub.length) {
                    const op = sub[i++]!;
                    const n = op === 0 || op === 1 ? 1 : op === 2 ? 2 : op === 3 ? 3 : 0;
                    out.push(op);
                    for (let k = 0; k < n; k++) { const x = sub[i++]!, y = sub[i++]!; out.push(a * x + c * y + dx, b * x + d * y + dy); }
                }
            }
            if (!(fl & 0x20)) break;
        }
        return out;
    }
}

function contour(out: GlyphPath, xs: number[], ys: number[], fl: number[], s: number, e: number) {
    const n = e - s + 1;
    if (n < 2) return;
    const on = (i: number) => (fl[s + (i % n)]! & 1) === 1;
    const X = (i: number) => xs[s + (i % n)]!, Y = (i: number) => ys[s + (i % n)]!;
    // a starting on-curve point
    let start = -1;
    for (let i = 0; i < n; i++) if (on(i)) { start = i; break; }
    let sx: number, sy: number;
    if (start < 0) { sx = (X(0) + X(1)) / 2; sy = (Y(0) + Y(1)) / 2; start = 0; out.push(0, sx, sy); for (let k = 1; k <= n; k++) { const cx = X(k), cy = Y(k), nx = (X(k) + X(k + 1)) / 2, ny = (Y(k) + Y(k + 1)) / 2; out.push(2, cx, cy, nx, ny); } out.push(4); return; }
    sx = X(start); sy = Y(start);
    out.push(0, sx, sy);
    let i = 1;
    while (i <= n) {
        const k = start + i;
        if (on(k)) { out.push(1, X(k), Y(k)); i++; }
        else {
            const cx = X(k), cy = Y(k);
            const nk = k + 1;
            let ex: number, ey: number;
            if (i + 1 > n || on(nk)) { ex = X(nk); ey = Y(nk); i += 2; }
            else { ex = (cx + X(nk)) / 2; ey = (cy + Y(nk)) / 2; i += 1; }
            out.push(2, cx, cy, ex, ey);
        }
    }
    out.push(4);
}
